import { useQuery } from '@apollo/client';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/core';
import {
  BaseText,
  BottomButtonsLayout,
  Button,
  Headline3,
  Spinner,
  theme,
} from 'aimo-ui';
import InfoList from 'components/common/InfoList';
import Icon from 'components/icons/Icon';
import WaitingNumber from 'components/longTermParking/waitingList/WaitingNumber';
import { SvgName } from 'components/svgImage/SvgImage';
import LongTermParkingContext from 'context/LongTermParkingContext';
import { READ_AVAILABLE_POSITION } from 'graphql/queries/readAvailablePosition';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ColumnWithMargins,
  ContainerWithGap,
  GreenBox,
  StyledWrapper,
} from 'styles/ContainerStyles';
import {
  Query,
  QueryReadAvailablePositionArgs,
} from 'types/generatedSchemaTypes';
import { LongTermNavProps, LongTermStackParamList } from 'types/navigation';

type InfoIcon = {
  icon: SvgName;
  rotate?: boolean;
  separator?: 1 | 2 | 3;
};

const infoArr: InfoIcon[] = [
  { icon: 'Services-Outlined' },
  { icon: 'Notification-Outlined', separator: 2 },
  { icon: 'check' },
  { icon: 'Car-Outlined', separator: 3 },
];

const ArrowRight = () => <Icon color={theme.colors.white} name="Arrow_Right" />;

const WaitingListInfoScreen: React.FC = () => {
  const { t } = useTranslation();
  const { navigate } = useNavigation<LongTermNavProps>();
  const { params: { pmcId, parkingZoneUid } = { pmcId: '' } } =
    useRoute<RouteProp<LongTermStackParamList, 'waitingListJoin'>>();

  const { selectedPmcs } = useContext(LongTermParkingContext);
  const pmc = selectedPmcs?.find((p) => p.pmcId === pmcId);

  const { data, loading } = useQuery<Query, QueryReadAvailablePositionArgs>(
    READ_AVAILABLE_POSITION,
    {
      variables: {
        input: {
          pmcId,
        },
      },
      fetchPolicy: 'network-only',
    }
  );

  const waitingCount = data?.readAvailablePosition?.position ?? 0;
  const daysCount =
    (pmc?.expirationLimits?.waitingListActivationPermitLock ?? 0) / 60 / 24;

  return (
    <BottomButtonsLayout
      contentBottom={
        <Button
          size="l"
          text={t('button.continue')}
          iconComponent={ArrowRight}
          iconRight
          onPress={() => navigate('waitingListJoin', { pmcId, parkingZoneUid })}
        />
      }>
      <ColumnWithMargins mt={20}>
        <ContainerWithGap>
          <BaseText>
            {t('longTermParking.waitingList.info.description')}
          </BaseText>
          <GreenBox>
            <StyledWrapper>
              <Headline3>
                {t('longTermParking.waitingList.info.title')}
              </Headline3>
            </StyledWrapper>
            <InfoList
              infoArr={infoArr}
              translationKeyPrefix="longTermParking.waitingList.info"
              translationValues={{ 2: daysCount }}
            />
          </GreenBox>
          {loading ? (
            <GreenBox>
              <Spinner />
            </GreenBox>
          ) : (
            <WaitingNumber
              waitingCount={waitingCount}
              orderStatus={'WAITING_FOR_ACTIVATION'}
            />
          )}
        </ContainerWithGap>
      </ColumnWithMargins>
    </BottomButtonsLayout>
  );
};

export default WaitingListInfoScreen;
